import { useState } from 'react';
import { ClipboardList, Rocket, ScanSearch } from 'lucide-react';
import { Button } from '@accio3d/design-system/button';
import { Checkbox } from '@accio3d/design-system/checkbox';
import { DialogDescription, DialogTitle } from '@accio3d/design-system/dialog';
import icon from '@accio3d/shared-ui/assets/arcessio-icon.svg';
import { useAuth } from '../../hooks/useAuth';
import { useAcceptSupplierTerms } from '../../hooks/useSupplierTerms';
import { SupplierOnboardingDialog } from './supplier-onboarding-dialog';

/** Introduces Arcessio and saves agreement acceptance before the second onboarding step. */
export function SupplierWelcomeModal({
  open,
  onOpenChange,
  onAccepted,
  url,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccepted: () => void;
  url: string;
}) {
  const { membership } = useAuth();
  const [checked, setChecked] = useState(false);
  const accept = useAcceptSupplierTerms();
  const canAccept = membership?.role === 'admin' || membership?.role === 'member';

  /** Saves the checked agreement before showing the supplier's next actions. */
  async function handleAccept() {
    if (!checked || !canAccept) return;
    try {
      await accept.mutateAsync(url);
      onAccepted();
    } catch {
      return;
    }
  }

  return (
    <SupplierOnboardingDialog open={open} onOpenChange={onOpenChange} step={1}>
      <div className="flex items-center gap-4">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-primary/15 bg-primary/5 shadow-sm sm:h-20 sm:w-20">
          <img src={icon} alt="" className="h-9 w-auto sm:h-11" />
        </span>
        <DialogTitle className="text-3xl font-semibold tracking-tight sm:text-4xl">
          Meet Arcessio
        </DialogTitle>
      </div>
      <p className="mt-4 text-lg font-medium text-primary">Your AI guide inside Arcessio</p>
      <DialogDescription className="mt-3 max-w-2xl text-sm leading-7 sm:text-base">
        Arcessio helps you review RFQs, analyze parts, prepare quotes, and move opportunities from
        request through production.
      </DialogDescription>
      <div className="my-8 grid gap-6 sm:grid-cols-3">
        {[
          {
            Icon: ClipboardList,
            title: 'Review opportunities',
            description: 'Understand incoming RFQs and what customers need.',
          },
          {
            Icon: ScanSearch,
            title: 'Analyze parts',
            description: 'Use Arcessio to evaluate CAD files and engineering drawings.',
          },
          {
            Icon: Rocket,
            title: 'Get things done',
            description: 'Create quotes, manage orders, and navigate with Arcessio guiding you.',
          },
        ].map(({ Icon, title, description }) => (
          <div key={title}>
            <Icon className="mb-3 h-5 w-5 text-primary" />
            <h3 className="text-sm font-semibold">{title}</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
          </div>
        ))}
      </div>
      <div className="border-t pt-6">
        {canAccept ? (
          <>
            <div className="flex items-start gap-3">
              <Checkbox
                id="supplier-terms-agreement"
                aria-label="I agree to the Terms of Use"
                checked={checked}
                onCheckedChange={(value) => setChecked(value === true)}
                disabled={accept.isPending}
                className="mt-0.5"
              />
              <div className="text-sm leading-5">
                <label htmlFor="supplier-terms-agreement" className="cursor-pointer">
                  I agree to the{' '}
                </label>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary underline underline-offset-4"
                >
                  Arcessio Supplier Terms of Use
                </a>
              </div>
            </div>
            {accept.isError && (
              <p role="alert" className="mt-3 text-sm text-destructive">
                We couldn’t save your agreement. Please try again.
              </p>
            )}
            <Button
              size="lg"
              className="mt-5 w-full sm:w-auto sm:min-w-56"
              disabled={!checked || accept.isPending}
              onClick={() => void handleAccept()}
            >
              {accept.isPending ? 'Saving agreement…' : 'Agree & continue'}
            </Button>
          </>
        ) : (
          <p className="text-sm leading-6 text-muted-foreground">
            Ask an organization admin or authorized member to accept our{' '}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary underline"
            >
              Terms of Use
            </a>{' '}
            to unlock RFQs.
          </p>
        )}
      </div>
    </SupplierOnboardingDialog>
  );
}
